// src/pages/web-settings.jsx

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  InputAdornment,
} from '@mui/material';

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';

// Social media icons (using MUI icons where available, others can be SVG or font-awesome later)
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/X';           // or use custom SVG for old twitter bird
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import MusicNoteIcon from '@mui/icons-material/MusicNote';     // for Spotify
import StarIcon from '@mui/icons-material/Star';               // placeholder for TripAdvisor / Yelp / Foursquare

import config from "../twh/config";           // adjust path
import { getFirstErrorMessage } from "../twh/functions"; // adjust path

const SectionHeader = ({ title }) => (
  <Typography
    variant="h5"
    fontWeight={700}
    color="#FF2C6D"
    gutterBottom
    sx={{ mb: 3, letterSpacing: 0.5 }}
  >
    {title}
  </Typography>
);

export default function WebSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Form fields ───────────────────────────────────────────────
  const [siteTitle, setSiteTitle] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');

  const [socialLinks, setSocialLinks] = useState({
    instagram_link: '',
    fourSquare_link: '',
    yelp_link: '',
    linkedin_link: '',
    spotify_link: '',
    tripAdvisor_link: '',
    twitter_link: '',
    facebook_link: '',
    youtube_link: '',
    email_address: '',
  });

  const [analytics, setAnalytics] = useState({
    google_analytics: '',
    google_tagManager: '',
  });

  // ── Fetch current settings ────────────────────────────────────
  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/web-settings/settings.php`,
        { credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.status === true && data.data?.length > 0) {
        const settings = data.data[0];

        setSiteTitle(settings.site_title || '');
        setLogoPreview(settings.logo ? `${config.reactApi_baseUrl}/media/images/logo/${settings.logo}` : '');
        setFaviconPreview(settings.favicon ? `${config.reactApi_baseUrl}/media/images/favicon/${settings.favicon}` : '');

        setSocialLinks({
          instagram_link: settings.instagram_link || '',
          fourSquare_link: settings.fourSquare_link || '',
          yelp_link: settings.yelp_link || '',
          linkedin_link: settings.linkedin_link || '',
          spotify_link: settings.spotify_link || '',
          tripAdvisor_link: settings.tripAdvisor_link || '',
          twitter_link: settings.twitter_link || '',
          facebook_link: settings.facebook_link || '',
          youtube_link: settings.youtube_link || '',
          email_address: settings.email_address || '',
        });

        setAnalytics({
          google_analytics: settings.google_analytics || '',
          google_tagManager: settings.google_tagManager || '',
        });
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // ── File handlers ─────────────────────────────────────────────
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      setFaviconPreview(URL.createObjectURL(file));
    }
  };

  // ── Submit handlers ───────────────────────────────────────────
  const handleSubmitHead = async () => {
    if (!siteTitle.trim()) {
      setError('Site title is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('issetAddEditWebsiteSettings', '1');
      formData.append('site_title', siteTitle.trim());
      if (logoFile) formData.append('logo', logoFile);
      if (faviconFile) formData.append('favicon', faviconFile);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/web-settings/add-edit-web-settings.php`,
        { method: 'POST', body: formData, credentials: 'include' }
      );

      const result = await res.json();

      if (result.status === true) {
        setSuccess('Header settings saved successfully');
        fetchSettings();
        setLogoFile(null);
        setFaviconFile(null);
      } else {
        setError(getFirstErrorMessage(result.errorMsg) || result.message || 'Save failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitSocial = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('issetAddEditSocialSettings', '1');

      Object.entries(socialLinks).forEach(([key, value]) => {
        formData.append(key, value.trim());
      });

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/web-settings/add-edit-social-settings.php`,
        { method: 'POST', body: formData, credentials: 'include' }
      );

      const result = await res.json();

      if (result.status === true) {
        setSuccess('Social links saved successfully');
        fetchSettings();
      } else {
        setError(getFirstErrorMessage(result.errorMsg) || result.message || 'Save failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitAnalytics = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('issetAddEditAnalyticsSettings', '1');
      formData.append('google_analytics', analytics.google_analytics.trim());
      formData.append('google_tagManager', analytics.google_tagManager.trim());

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/web-settings/add-edit-analytics-settings.php`,
        { method: 'POST', body: formData, credentials: 'include' }
      );

      const result = await res.json();

      if (result.status === true) {
        setSuccess('Analytics settings saved successfully');
        fetchSettings();
      } else {
        setError(getFirstErrorMessage(result.errorMsg) || result.message || 'Save failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        WEBSITE SETTINGS
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* HEAD SECTION */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
            <SectionHeader title="HEAD" />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Site Title"
                  value={siteTitle}
                  onChange={(e) => setSiteTitle(e.target.value)}
                  variant="outlined"
                  helperText="This appears in browser title & search results"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Logo
                  </Typography>
                  {logoPreview && (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Logo Preview"
                      sx={{
                        maxHeight: 64,
                        maxWidth: 100,
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 2,
                        p: 1,
                        mb: 1,
                      }}
                    />
                  )}
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Upload Logo
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      hidden
                      onChange={handleLogoChange}
                    />
                  </Button>
                  {logoFile && (
                    <Typography variant="caption" color="text.secondary">
                      Selected: {logoFile.name}
                    </Typography>
                  )}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Favicon
                  </Typography>
                  {faviconPreview && (
                    <Box
                      component="img"
                      src={faviconPreview}
                      alt="Favicon Preview"
                      sx={{
                        width: 64,
                        height: 64,
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 2,
                        p: 1,
                      }}
                    />
                  )}
                  
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Upload Favicon
                    <input
                      type="file"
                      accept="image/png,image/x-icon"
                      hidden
                      onChange={handleFaviconChange}
                    />
                  </Button>
                  {faviconFile && (
                    <Typography variant="caption" color="text.secondary">
                      Selected: {faviconFile.name}
                    </Typography>
                  )}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ mt: 3, textAlign:'right', }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmitHead}
                  disabled={saving}
                  sx={{ px: 5, py: 1.2 }}
                >
                  Save Changes
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* SOCIAL LINKS SECTION */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
            <SectionHeader title="SOCIAL LINKS" />

            <Grid container spacing={2}>
              {Object.entries(socialLinks).map(([key, value]) => {
                const iconMap = {
                  instagram_link: <InstagramIcon color="action" />,
                  fourSquare_link: <StarIcon color="action" />,           // placeholder
                  yelp_link: <StarIcon color="action" />,                 // placeholder
                  linkedin_link: <LinkedInIcon color="action" />,
                  spotify_link: <MusicNoteIcon color="action" />,
                  tripAdvisor_link: <StarIcon color="action" />,          // placeholder
                  twitter_link: <TwitterIcon color="action" />,
                  facebook_link: <FacebookIcon color="action" />,
                  youtube_link: <YouTubeIcon color="action" />,
                  email_address: <EmailIcon color="action" />,
                };

                const labelMap = {
                  instagram_link: 'Instagram',
                  fourSquare_link: 'FourSquare',
                  yelp_link: 'Yelp',
                  linkedin_link: 'LinkedIn',
                  spotify_link: 'Spotify',
                  tripAdvisor_link: 'TripAdvisor',
                  twitter_link: 'Twitter / X',
                  facebook_link: 'Facebook',
                  youtube_link: 'YouTube',
                  email_address: 'Email',
                };

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 6 }} key={key}>
                    <TextField
                      fullWidth
                      label={labelMap[key]}
                      value={value}
                      onChange={(e) =>
                        setSocialLinks((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {iconMap[key] || <StarIcon color="action" />}
                          </InputAdornment>
                        ),
                      }}
                      placeholder={key.includes('link') ? 'https://...' : 'example@email.com'}
                    />
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ mt: 4, textAlign: { xs: 'center', sm: 'right' } }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSubmitSocial}
                disabled={saving}
                sx={{ minWidth: 160 }}
              >
                Save Social Links
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* ANALYTICS SECTION */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 3 }}>
            <SectionHeader title="ANALYTICS" />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Google Analytics (Tracking Code / GA4)"
                  value={analytics.google_analytics}
                  onChange={(e) =>
                    setAnalytics((prev) => ({ ...prev, google_analytics: e.target.value }))
                  }
                  variant="outlined"
                  helperText="Paste your GA4 Measurement ID or full script"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Google Tag Manager"
                  value={analytics.google_tagManager}
                  onChange={(e) =>
                    setAnalytics((prev) => ({ ...prev, google_tagManager: e.target.value }))
                  }
                  variant="outlined"
                  helperText="Paste your GTM container code (GTM-XXXXXX)"
                />
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ mt: 2, textAlign:'right', }} >
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSubmitAnalytics}
                  disabled={saving}
                  sx={{ minWidth: 180 }}
                >
                  Save Analytics
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}